import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  const allowedCodes = new Set([200, 201, 204]);

  if (!payload || !allowedCodes.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  return payload;
};

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await parseEnvelope(response);

      return {
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in,
        username,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Unable to login. Please try again.");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { getState, rejectWithValue }) => {
    const { accessToken, refreshToken } = getState().auth;

    if (!refreshToken) {
      return true;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      await parseEnvelope(response);
      return true;
    } catch (error) {
      return rejectWithValue(error.message || "Unable to logout.");
    }
  }
);

const resetAuthState = (state) => {
  state.user = null;
  state.status = "idle";
  state.error = null;
  state.accessToken = null;
  state.refreshToken = null;
  state.expiresIn = null;
};

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    status: "idle",
    error: null,
    accessToken: null,
    refreshToken: null,
    expiresIn: null,
  },
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    forceLogout: resetAuthState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = {
          username: action.payload.username,
          name: action.payload.username,
          role: "Administrator",
        };
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresIn = action.payload.expiresIn;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Unable to login. Please try again.";
      })
      .addCase(logoutUser.fulfilled, (state) => {
        resetAuthState(state);
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload || "Unable to logout.";
        resetAuthState(state);
      });
  },
});

export const { clearAuthError, forceLogout } = authSlice.actions;

export const selectIsAuthenticated = (state) => Boolean(state.auth.user);
export const selectAuthUser = (state) => state.auth.user;
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;
export const selectAccessToken = (state) => state.auth.accessToken;

export default authSlice.reducer;
